(ns common.schema.entities.workspace.kyc.questions.controlling-person
  (:require
   [common.country :as country]
   [common.util :as cu]))

(def us-tax-status-options
  [{:key :us-citizen
    :ord 1.0
    :translations {:en "The Controlling Person is a U.S. citizen and/or is resident in the United States for tax purposes (including a green card holder or a person meeting the IRS substantial presence test)."}}
   {:key :ex-citizen
    :ord 2.0
    :translations {:en "The Controlling Person was born in the United States (or a U.S. territory) but is no longer a U.S. citizen because citizenship has been voluntarily relinquished."}}
   {:key :not-us-tax-resident
    :ord 3.0
    :translations {:en "The Controlling Person is not a U.S. citizen and is not resident in the United States for tax purposes."}}])

(def tax-reference-type-options
  [{:key :tax-reference-type/tin
    :ord 1.0
    :translations {:en "TIN"}}
   {:key :tax-reference-type/ssn
    :ord 2.0
    :translations {:en "SSN"}}
   {:key :tax-reference-type/itin
    :ord 3.0
    :translations {:en "ITIN"}}
   {:key :tax-reference-type/ein
    :ord 4.0
    :translations {:en "EIN"}}
   {:key :tax-reference-type/other
    :ord 5.0
    :translations {:en "Other"}}])

(def control-type-options
  [{:key :control-type/ownership-25-percent-or-more
    :ord 1.0
    :translations {:en "Direct or indirect ownership of 25% or more"}}
   {:key :control-type/board-appointment-rights
    :ord 2.0
    :translations {:en "Right to appoint/remove majority of board"}}
   {:key :control-type/significant-influence
    :ord 3.0
    :translations {:en "Exercise significant influence"}}
   {:key :control-type/trust-beneficiary-with-25-percent-or-more-interest
    :ord 4.0
    :translations {:en "Beneficiary of trust with 25% or more interest"}}
   {:key :control-type/officer-director
    :ord 5.0
    :translations {:en "Officer/director with executive authority"}}
   {:key :control-type/partnership-interest-of-25-percent-or-more
    :ord 6.0
    :translations {:en "Partnership interest of 25% or more"}}
   {:key :control-type/general-partner
    :ord 7.0
    :translations {:en "General partner"}}
   {:key :control-type/managing-member
    :ord 8.0
    :translations {:en "Managing member"}}
   {:key :control-type/trustee-investment-discretion
    :ord 9.0
    :translations {:en "Trustee with investment discretion"}}
   {:key :control-type/investment-advisor
    :ord 10.0
    :translations {:en "Investment advisor"}}
   {:key :control-type/other-control-arrangement
    :ord 11.0
    :translations {:en "Other control arrangement"}}
   {:key :control-type/ceo-equivalent
    :ord 12.0
    :translations {:en "CEO/equivalent"}}
   {:key :control-type/other
    :ord 13.0
    :translations {:en "Other"}}])

(def type-of-controlling-person-edn-options
  (->> [{:value :controlling-person-of-legal-entity/control-by-ownership
         :translations {:en "Controlling Person of a legal person - control by ownership"}}
        {:value :controlling-person-of-legal-entity/control-by-other-means
         :translations {:en "Controlling Person of a legal person - control by other means"}}
        {:value :controlling-person-of-legal-entity/senior-managing-official
         :translations {:en "Controlling Person of a legal person - senior managing official"}}
        {:value :controlling-person-of-trust/settlor
         :translations {:en "Controlling Person of a trust - settlor"}}
        {:value :controlling-person-of-trust/trustee
         :translations {:en "Controlling Person of a trust - trustee"}}
        {:value :controlling-person-of-trust/protector
         :translations {:en "Controlling Person of a trust - protector"}}
        {:value :controlling-person-of-trust/beneficiary
         :translations {:en "Controlling Person of a trust - beneficiary"}}
        {:value :controlling-person-of-trust/other
         :translations {:en "Controlling Person of a trust - other"}}
        {:value :controlling-person-of-non-trust-legal-arrangement/settlor-equivalent
         :translations {:en "Controlling Person of a legal arrangement (non-trust) - settlor-equivalent"}}
        {:value :controlling-person-of-non-trust-legal-arrangement/trustee-equivalent
         :translations {:en "Controlling Person of a legal arrangement (non-trust) - trustee-equivalent"}}
        {:value :controlling-person-of-non-trust-legal-arrangement/protector-equivalent
         :translations {:en "Controlling Person of a legal arrangement (non-trust) - protector-equivalent"}}
        {:value :controlling-person-of-non-trust-legal-arrangement/beneficiary-equivalent
         :translations {:en "Controlling Person of a legal arrangement (non-trust) - beneficiary-equivalent"}}
        {:value :controlling-person-of-non-trust-legal-arrangement/other-equivalent
         :translations {:en "Controlling Person of a legal arrangement (non-trust) - other-equivalent"}}]
       (mapv (fn [opt]
               (assoc opt :key (:value opt))))))

(def all-questions
  {:kyc-controlling-person/last-name
   {:data/type :data.type/text-simple
    :translations {:en "Last name"}}

   :kyc-controlling-person/first-name
   {:data/type :data.type/text-simple
    :translations {:en "First name"}}

   :kyc-controlling-person/middle-names
   {:data/type :data.type/text-simple
    :translations {:en "Middle name(s)"}}

   :kyc-controlling-person/date-of-birth
   {:data/type :data.type/date
    :translations {:en "Date of birth"}}

   :kyc-controlling-person/place-of-birth
   {:data/type :data.type/text-simple
    :translations {:en "Place of birth (Town/City)"}}

   :kyc-controlling-person/country-of-birth
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "Country of birth"}}

   :kyc-controlling-person/street-address
   {:data/type :data.type/text-simple
    :translations {:en "Street address"}}

   :kyc-controlling-person/city
   {:data/type :data.type/text-simple
    :translations {:en "City"}}

   :kyc-controlling-person/country
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "Country"}}

   :kyc-controlling-person/zip-code
   {:data/type :data.type/text-simple
    :translations {:en "Zip code"}}

   :kyc-controlling-person/mailing-street-address
   {:data/type :data.type/text-simple
    :translations {:en "Mailing street address"}}

   :kyc-controlling-person/mailing-city
   {:data/type :data.type/text-simple
    :translations {:en "Mailing city/town"}}

   :kyc-controlling-person/mailing-country
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "Mailing country"}}

   :kyc-controlling-person/mailing-zip-code
   {:data/type :data.type/text-simple
    :translations {:en "Mailing zip code"}}

   :kyc-controlling-person/us-tax-status
   {:data/type :data.type/keyword
    :editor/type :editor.type/radio
    :options us-tax-status-options
    :translations {:en "Please select one of the following declarations and complete any follow-up fields."}}

   :kyc-controlling-person/us-tin
   {:data/type :data.type/text-simple
    :translations {:en "U.S. federal taxpayer identifying number (U.S. TIN)"}}

   :kyc-controlling-person/jurisdiction-of-tax-residence
   {:data/type :data.type/keyword
    :options country/options
    :translations {:en "Jurisdiction of tax residence"}}

   :kyc-controlling-person/tax-countries
   {:data/type        :data.type/text-edn
    :edn-data/type    :edn-data.type/tax-recidencies
    :options          country/options
    :mandatory-field? (fn [_answers kyc-process]
                        (not= (:kyc-process/status kyc-process)
                              :kyc-process.status/draft))
    :translations     {:en "Countries of tax residence"
                       :fi "Verotusmaat"}}

   :kyc-controlling-person/tax-countries-info
   {:data/type       :data.type/info
    :optional-field? true
    :translations
    {:en "Please complete the following table indicating: (i) where the Controlling Person is tax resident; (ii) the Controlling Person's TIN for each jurisdiction indicated; and (iii) if the Controlling Person is a tax resident in a jurisdiction that is a Reportable Jurisdiction(s) then please also complete the section \"Type of Controlling Person\" below."}}

   :kyc-controlling-person/tax-reference-number-type
   {:data/type :data.type/keyword
    :options tax-reference-type-options
    :conditional-field (fn [answers]
                         (:kyc-controlling-person/jurisdiction-of-tax-residence answers))
    :mandatory-field? (fn [answers]
                        (:kyc-controlling-person/jurisdiction-of-tax-residence answers))
    :translations {:en "Tax reference number type"}}

   :kyc-controlling-person/tax-reference-number
   {:data/type :data.type/text-simple
    :conditional-field (fn [answers]
                         (:kyc-controlling-person/jurisdiction-of-tax-residence answers))
    :mandatory-field? (fn [answers]
                        (:kyc-controlling-person/jurisdiction-of-tax-residence answers))
    :translations {:en "Tax reference number"}}

   :kyc-controlling-person/reason-for-tin-unavailability
   {:data/type :data.type/text-simple
    :conditional-field (fn [answers]
                         (and (:kyc-controlling-person/jurisdiction-of-tax-residence answers)
                              (cu/empty-value? (:kyc-controlling-person/tax-reference-number answers))))
    :translations {:en "Reason for non-availability of a TIN"}}

   :kyc-controlling-person/type-of-control
   {:data/type :data.type/keyword
    :options control-type-options
    :translations {:en "Type of control over the entity"}}

   :kyc-controlling-person/declaration-of-accuracy
   {:data/type :data.type/confirm
    :translations {:en "Declaration of accuracy"}}

   :kyc-controlling-person/declaration-to-notify-changes
   {:data/type :data.type/confirm
    :translations {:en "Declaration to notify changes"}}

   :kyc-controlling-person/signature
   {:data/type :data.type/text-simple
    :translations {:en "Signature of Controlling Person"}}

   :kyc-controlling-person/print-name
   {:data/type :data.type/text-simple
    :translations {:en "Print name"}}

   :kyc-controlling-person/signature-date
   {:data/type :data.type/date
    :translations {:en "Date (dd/mm/yyyy)"}}

   :kyc-controlling-person/capacity-if-not-signed-by-cp
   {:data/type :data.type/text-simple
    :translations {:en "Capacity (if not signed by CP)"}}

   :kyc-controlling-person/lifeline-type-of-controlling-person-info
   {:data/type :data.type/info
    :optional-field? true
    :translations {:en "Please only complete this section if the Controlling Person is tax resident in one or more Reportable Jurisdictions"}}

   :kyc-controlling-person/lifeline-type-of-controlling-person-edn
   {:data/type     :data.type/text-edn
    :edn-data/type :edn-data.type/type-of-controlling-person
    :info          {:en "Assign one or more roles to the Controlling Person for each entity listed in section \"Relevant Entity Account Holder(s)\""}
    :translations  {:en "Type of controlling person"}
    :options       type-of-controlling-person-edn-options}

   :kyc-controlling-person/lifeline-legal-names-of-the-entity-account-holders-for-which-is-controlling-person
   {:data/type :data.type/text-edn
    :edn-data/type :edn-data.type/string-array
    :placeholder  {:en "Legal name of entity"}
    :translations {:en "Enter the legal name(s) of the Entity Account Holder(s) for which this individual is a Controlling Person"}}})
